import React, { useState } from "react";
import { MdWarningAmber } from "react-icons/md";

import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import SheetDialog from "./SheetDialog";

// What's typed to cancel a run that's under way.
export const CONFIRM_WORD = "CANCEL";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Cancel run, from the route sheet: it asks first. The sheet shows it only
 * while it's open, so each time starts afresh. A run under way (`underWay`:
 * stops recorded, `recorded` of `stopCount` as far as the sheet knows) says
 * how many and waits for CANCEL to be typed. `onConfirm()` cancels it (the
 * sheet sends confirm_under_way when it's under way). `blocked` says why it
 * can't be cancelled now (locked, say); `error` is why the API refused.
 */
const CancelRunDialog = ({
  runName,
  stopCount = 0,
  recorded = 0,
  underWay = false,
  blocked = "",
  busy = false,
  error = "",
  onConfirm,
  onClose,
}) => {
  const [typed, setTyped] = useState("");
  const confirmed = !underWay || typed.trim() === CONFIRM_WORD;

  return (
    <SheetDialog
      open
      labelId="cancelRunTitle"
      title="Cancel this run?"
      subtitle={runName}
      icon={MdWarningAmber}
      closeDisabled={busy}
      onClose={onClose}
      footer={
        <>
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Keep run"
            onClick={onClose}
            disabled={busy}
          />
          <BoltButton
            type="button"
            variant="danger"
            size="sm"
            label={busy ? "Cancelling…" : "Yes, cancel run"}
            onClick={onConfirm}
            disabled={busy || Boolean(blocked) || !confirmed}
          />
        </>
      }
    >
      {underWay ? (
        <>
          <p className="m-[0px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg p-[10px] text-[12.5px] text-state-warning-text">
            This run is under way:{" "}
            {recorded > 0
              ? `${recorded} of ${plural(stopCount, "stop")} ${recorded === 1 ? "is" : "are"} recorded`
              : "stops on it are recorded already"}
            . Cancelling closes it as it is, so nothing more can be recorded on
            it. The orders stay as they are.
          </p>
          <label className="grid gap-[4px]">
            <span className="text-[12px] text-ui-text">
              Type <b>{CONFIRM_WORD}</b> to confirm
            </span>
            <input
              type="text"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={busy}
              autoComplete="off"
              spellCheck={false}
              data-autofocus
              className="w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[9px] py-[6px] text-[12.5px] text-ui-text focus:border-brand focus:outline-none"
            />
          </label>
        </>
      ) : (
        <p className="m-[0px] text-[12.5px] text-ui-text">
          The orders stay as they are, free to go on another run.
        </p>
      )}
      {error ? (
        <p
          role="alert"
          className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[8px] text-[12px] text-state-danger-text"
        >
          {error}
        </p>
      ) : (
        blocked && (
          <p className="m-[0px] text-[12px] text-ui-text-muted">{blocked}</p>
        )
      )}
    </SheetDialog>
  );
};

export default CancelRunDialog;
